import { useCallback, useEffect, useRef, useState } from "react";
import {
  getTurnstileState,
  subscribeTurnstile,
  resetTurnstileState,
  prewarmTurnstile,
  turnstileConfigured,
  verifyHuman,
  type TurnstileAction,
  type TurnstileRequest,
  type TurnstileResult,
  type TurnstileState,
} from "@/lib/auth/turnstile";

/**
 * React binding for the Cloudflare bot check.
 *
 * Gives forms three things: a `verify()` that returns the token (or null),
 * live `state` for the loading/challenge/error notice, and a `retry()` that
 * re-runs the last action with a visible challenge.
 */
export function useTurnstile(options?: { prewarm?: boolean; prewarmAction?: TurnstileAction }) {
  const [state, setState] = useState<TurnstileState>(() => getTurnstileState());
  const lastRequest = useRef<TurnstileRequest | null>(null);

  useEffect(() => subscribeTurnstile(setState), []);

  useEffect(() => {
    if (options?.prewarm === false || !turnstileConfigured()) return;
    // With an action we run the whole silent check up front, so the first
    // submit never races an unfinished check.
    prewarmTurnstile(options?.prewarmAction);
  }, [options?.prewarm, options?.prewarmAction]);

  // Don't leave a stale "failed"/"passed" badge behind on unmount.
  useEffect(() => () => resetTurnstileState(), []);

  const verify = useCallback(async (req: TurnstileRequest): Promise<TurnstileResult> => {
    lastRequest.current = req;
    return verifyHuman(req);
  }, []);

  /** Convenience for call sites that only need the token. */
  const verifyToken = useCallback(
    async (action: TurnstileAction, interactive = false): Promise<string | null> =>
      (await verify({ action, interactive })).token,
    [verify],
  );

  const retry = useCallback(async (): Promise<TurnstileResult | null> => {
    const req = lastRequest.current;
    if (!req) return null;
    return verify({ ...req, interactive: true });
  }, [verify]);

  return {
    state,
    status: state.status,
    reason: state.reason,
    configured: turnstileConfigured(),
    verify,
    verifyToken,
    retry,
    reset: resetTurnstileState,
  };
}
